import styled, { css } from 'styled-components';
import Text from '@components/text';
import Button from '@components/button';
import { generateScales } from '@helpers/generate-media-queries';
import Input from '@components/input';
import TextArea from '@components/text-area';
import Section from '@components/section';
import { underlineTransition } from '@components/link/link.styled';
import { LargeButton } from '@styles/button.styled';

export const HelperText = styled.div`
  ${({ theme }) => css`
    font-family: ${theme.fontFamily.additional};
    color: ${theme.colors.tertiary};
    font-size: ${theme.fontSize.small.desktop};
    margin-top: 0.5rem;
  `}
`;

export const InfoText = styled(Text)`
  text-align: center;
`;

export const ContactSection = styled(Section)`
  margin-top: 5.5rem;
`;

export const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 2rem;
    max-width: 768px;
    margin: 0 auto;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      flex-direction: column;
      row-gap: 4rem;
    }
  `}
`;

export const HelperContainer = styled.div`
  ${({ theme }) => css`
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    row-gap: 2.5rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      display: none;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      flex-direction: row;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      margin-top: 2rem;
      margin-left: auto;
      margin-right: auto;
      flex-direction: column;
      row-gap: 2.5rem;
    }
  `}
`;

export const FooterContainer = styled.div`
  ${({ theme }) => css`
    display: none;
    flex-direction: row;
    column-gap: 2.5rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      display: flex;
      column-gap: 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      flex-direction: column;
      row-gap: 2.5rem;
    }
  `}
`;

export const HelperItemContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    column-gap: 0.75rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      flex-direction: column;
      row-gap: 1rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      flex-direction: row;
    }
  `}
`;

export const ImageContainer = styled.div`
  height: 3rem;
  max-width: 3rem;
  width: 100%;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 1rem;
`;

export const StyledReCaptcha = styled.div`
  display: flex;
  justify-content: center;
`;

export const HelperTextContainer = styled.div`
  ${({ theme }) => css`
    max-width: 19.25rem;
    @media only screen and (max-width: ${theme.breakpoints.md}) {
      max-width: 100%;
    }
  `}
`;

export const StyledTitle = styled(Text)`
  ${({ theme }) => css`
    margin-bottom: 0.5rem;
    ${generateScales(
      'font-size',
      theme.fontSize.heading6.mobile,
      theme.fontSize.body.desktop,
      theme
    )};
    color: ${theme.colors.black};
  `}
`;

export const StyledDescription = styled(Text)`
  ${({ theme }) => css`
    a {
      ${underlineTransition}
      color: ${theme.colors.secondary};
    }
    ${generateScales(
      'font-size',
      theme.fontSize.small.mobile,
      theme.fontSize.small.desktop,
      theme
    )};
  `}
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  row-gap: 2rem;
  width: 100%;
  flex: 1;
`;

export const FormRow = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 2rem;
    row-gap: 2rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      grid-template-columns: 1fr;
    }
  `}
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledText = styled(Text)`
  ${({ theme }) => css`
    font-family: ${theme.fontFamily.additional};
    margin-bottom: 0.5rem;
    font-weight: ${theme.fontWeights.normal};
    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.small.desktop, theme)}
  `}
`;

export const MessageContainer = styled.div`
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
  max-width: 40rem;
  text-align: center;
`;

export const StyledInput = styled(Input)`
  ${({ theme }) => css`
    width: 100%;
    padding-left: 0.5rem;
    border-radius: 0.125rem;
    border: 1px solid ${theme.colors.light};
    color: ${theme.colors.primary};
    max-height: 2.75rem;

    :focus {
      border-color: ${theme.colors.dark};
    }

    ::-webkit-input-placeholder {
      color: ${theme.colors.secondary};
    }

    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)};
  `}
`;

export const StyledTextArea = styled(TextArea)`
  ${({ theme }) => css`
    width: 100%;
    border-radius: 0.125rem;
    border: 1px solid ${theme.colors.light};
    color: ${theme.colors.primary};

    :focus {
      border-color: ${theme.colors.dark};
    }

    ::-webkit-input-placeholder {
      color: ${theme.colors.secondary};
    }

    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)};
  `}
`;

// export const StyledSelect = styled(Select)`
//   width: 100%;
// `;

export const StyledSelect = styled.select`
  ${({ theme }) => css`
    width: 100%;
    padding-left: 0.5rem;
    border-radius: 0.125rem;
    border: 1px solid ${theme.colors.light};
    color: ${theme.colors.primary};
    height: 2.75rem;
    max-height: 2.75rem;
    background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAUCAMAAACtdX32AAAAdVBMVEUAAAD///8AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAhMdQaAAAAJ3RSTlMAAAECAwQGBwsOFBwkJTg5RUZ4eYCHkJefpaytrsXGy8zW3+Do8vNn0bsyAAAAYElEQVR42tXROwJDQAAA0Ymw1p9kiT+L5P5HVEi3qJn2lcPjtIuzUIJ/rhIGy762N3XaThqMN1ZPALsZPEzG1x8LrFL77DHBnEMxBewz0fJ6LyFHTPL7xhwzWYrJ9z22AqmQBV757MHfAAAAAElFTkSuQmCC);
    background-position: 100%;
    background-repeat: no-repeat;
    appearance: none;

    :focus {
      border-color: ${theme.colors.dark};
    }

    ::-webkit-input-placeholder {
      color: ${theme.colors.secondary};
    }

    :invalid {
      color: ${theme.colors.secondary};
    }

    ::after {
      display: block;
      content: '▼';
      z-index: 9;
      position: absolute;
      right: 0;
      top: 0;
      padding-right: 0.5rem;
      padding-top: 0.5rem;
      pointer-events: none;
      color: ${theme.colors.primary};
    }

    :required:invalid {
      color: ${theme.colors.secondary};
    }

    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)};
  `}
`;

export const StyledButton = styled(Button)`
  ${({ theme }) => css`
    width: 100%;
    color: ${theme.colors.primary};
    max-width: 16rem;
    border-radius: ${theme.borderRadius.large};
    font-weight: 500;
    border: none;
    margin: 0 auto;
    background-color: ${theme.colors.accent};

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      max-width: 100%;
    }

    :hover {
      background-color: ${theme.colors.primary};
      color: ${theme.colors.white};
    }
  `}
`;

export const HubSpotForm = styled.div`
  ${({ theme }) => css`
    .hs-form {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      row-gap: 1.5rem;
      column-gap: 1.5rem;
      width: 100%;
      background-color: ${theme.colors.light}66;
      border-radius: ${theme.borderRadius.large};
      padding: 2rem 3rem 3rem 3rem;
      max-width: 44rem;
    }

    .hs_submit {
      width: 100%;
    }

    .actions,
    .hs_recaptcha {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    .hs-button {
      ${LargeButton}
      font-weight: 400;
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primary};
      color: ${theme.colors.white};
      min-width: 14rem;

      :hover {
        border-color: ${theme.colors.black};
        background: ${theme.colors.black};
        color: ${theme.colors.white};
      }

      :focus {
        outline: 4px solid ${theme.colors.white}44;
      }
    }

    .form-columns-1 {
      display: flex;
      flex-direction: column;
      row-gap: 1.5rem;
      column-gap: 1.5rem;
      width: 100%;
      max-width: unset;
    }

    .form-columns-2 {
      display: flex;
      flex-direction: row;
      row-gap: 1.5rem;
      column-gap: 1.5rem;
      width: 100%;
      max-width: unset;

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        flex-direction: column;
      }
    }

    .hs-form-field {
      display: flex;
      flex-direction: column;
      row-gap: 0.0625rem;
      width: 100%;
      max-width: unset;

      .input {
        margin-right: 0 !important;
      }

      input,
      select {
        border-radius: 0.125rem;
        ${generateScales(
          'font-size',
          theme.inputSize.lg.mobile.fontSize,
          theme.inputSize.lg.desktop.fontSize,
          theme
        )}
        ${generateScales('padding-left', '0.5rem', '0.75rem', theme)}
        ${generateScales('padding-right', '0.5rem', '0.75rem', theme)}
        ${generateScales('height', '2.75rem', '3rem', theme)}

        border: 1px solid ${theme.colors.darkPurple};
        background-color: ${theme.colors.white};
        color: ${theme.colors.black};
        outline: none;
        font-family: ${theme.fontFamily.text};
        transition: all ${theme.transitions.normal};
        border-radius: 0.125rem;
        width: 100% !important;

        ::placeholder {
          color: ${theme.colors.dark}99;
        }

        :focus {
          border-color: ${theme.colors.dark};
          outline: 4px solid ${theme.colors.secondary}22;
        }
      }

      textarea {
        border-radius: 0.125rem;
        ${generateScales(
          'font-size',
          theme.inputSize.lg.mobile.fontSize,
          theme.inputSize.lg.desktop.fontSize,
          theme
        )}
        ${generateScales('padding-left', '0.5rem', '0.75rem', theme)}
        ${generateScales('padding-right', '0.5rem', '0.75rem', theme)}
        ${generateScales('height', '8.25rem', '9rem', theme)}

        border: 1px solid ${theme.colors.darkPurple};
        background-color: ${theme.colors.white};
        color: ${theme.colors.darkPurple};
        outline: none;
        font-family: ${theme.fontFamily.text};
        transition: all ${theme.transitions.normal};
        border-radius: 0.125rem;
        width: 100% !important;
        max-width: 40rem;

        ::placeholder {
          color: ${theme.colors.dark}99;
        }

        :focus {
          border-color: ${theme.colors.dark};
          outline: 4px solid ${theme.colors.secondary}22;
        }
      }

      label {
        font-weight: 400;
        font-family: ${theme.fontFamily.text};
        font-size: ${theme.fontSize.extraSmall.desktop};
        line-height: ${theme.lineHeights.xs};
        color: ${theme.colors.black};
        margin: 0 0 ${theme.spacing.xs} 0;

        &.hs-error-msg {
          margin-top: 0.5rem;
          font-family: ${theme.fontFamily.additional};
          color: red;
          font-size: ${theme.fontSize.extraSmall.desktop};
        }
      }
    }
  `}
`;
